import { Tree } from "fluid-framework";
import { useEffect, useRef } from "react";

import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useAuth } from "../auth/use-auth.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";
import { useCurrentUserTablePresence } from "../presence/use-my-table-presence.js";
import {
  selectPreferredSpeakerId,
  useDevicesStore,
} from "../table-call/audio-devices.store.js";
import { useJoinChimeStore } from "./join-chime.store.js";
import {
  getCurrentTableParticipantIds,
  getNewParticipantIds,
} from "./table-join-chime.helpers.js";

const CHIME_SRC = "/audio/join-table-chime.wav";

type AudioElementWithSinkId = HTMLAudioElement & {
  setSinkId: (sinkId: string) => Promise<void>;
};

const toError = (error: unknown, fallbackMessage: string) =>
  error instanceof Error ? error : new Error(fallbackMessage);

export const useTableJoinChime = () => {
  const { user } = useAuth();
  const { sharedTree } = useFluid();
  const { tableId } = useCurrentUserTablePresence();
  const enabled = useJoinChimeStore((state) => state.enabled);
  const preferredSpeakerId = useDevicesStore(selectPreferredSpeakerId);

  const baselineParticipantIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const participantIds = getCurrentTableParticipantIds({
      sharedTree,
      tableId,
      userId: user.id,
    });

    if (!(enabled && participantIds)) {
      baselineParticipantIdsRef.current = new Set();
      return;
    }

    baselineParticipantIdsRef.current = participantIds;
  }, [enabled, sharedTree, tableId, user.id]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const playChime = () => {
      const audio = new Audio(CHIME_SRC);
      audio.preload = "auto";
      const cleanupAudio = () => {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      };

      audio.addEventListener("ended", cleanupAudio, { once: true });

      const playback =
        preferredSpeakerId && "setSinkId" in audio
          ? (audio as AudioElementWithSinkId).setSinkId(preferredSpeakerId)
          : Promise.resolve();

      playback
        .then(() => audio.play())
        .catch((error: unknown) => {
          cleanupAudio();
          recordErrorInSentry(
            toError(error, "Table join chime playback failed"),
            {
              extra: { preferredSpeakerId },
              tags: { feature: "table-join-chime" },
            }
          );
        });
    };

    const handleTreeChanged = () => {
      try {
        const currentParticipantIds = getCurrentTableParticipantIds({
          sharedTree,
          tableId,
          userId: user.id,
        });

        if (!currentParticipantIds) {
          baselineParticipantIdsRef.current = new Set();
          return;
        }

        const previousParticipantIds = baselineParticipantIdsRef.current;
        const newParticipantIds = getNewParticipantIds({
          currentParticipantIds,
          ownUserId: user.id,
          previousParticipantIds,
        });

        baselineParticipantIdsRef.current = currentParticipantIds;

        if (newParticipantIds.length === 0) {
          return;
        }

        for (const _participantId of newParticipantIds) {
          playChime();
        }
      } catch (error: unknown) {
        recordErrorInSentry(toError(error, "Table join chime handler failed"), {
          tags: { feature: "table-join-chime" },
        });
      }
    };

    const unsubscribe = Tree.on(
      sharedTree.root,
      "treeChanged",
      handleTreeChanged
    );

    return () => {
      unsubscribe();
    };
  }, [enabled, preferredSpeakerId, sharedTree, tableId, user.id]);
};
